'use client'

import { useSyncExternalStore, type ReactElement } from 'react'
import type { ChatState } from '@/lib/santa-chat/chat-state'
import type { SantaChatCopy } from './santa-chat-screen'
import { Cable, EarOff, Hourglass, MailX, MoonStar, type LucideIcon } from 'lucide-react'
import { formatRetryDuration, secondsUntil } from '@/lib/santa-chat/retry-time'

/** El aviso de espera se recalcula cada medio minuto mientras está en pantalla. */
const CLOCK_TICK_MS = 30_000
let clockNow = Date.now()

function subscribeToClock(onTick: () => void): () => void {
  clockNow = Date.now()
  const timer = setInterval(() => {
    clockNow = Date.now()
    onTick()
  }, CLOCK_TICK_MS)
  return () => clearInterval(timer)
}

function readClock(): number {
  return clockNow
}

/** «Ahora» que avanza solo: la espera es una función pura de (instante, ahora). */
function useClock(): number {
  return useSyncExternalStore(subscribeToClock, readClock, readClock)
}

interface ChatNoticeProps {
  /** Dibujo grande arriba: quien no sabe leer entiende el aviso por él. */
  icon: LucideIcon
  title: string
  body: string
  role: 'status' | 'alert'
  /** A second line under the body (the free tier's packs offer). */
  note?: string
  action?: { label: string; onClick: () => void }
}

/** Aviso con la voz de Papá Noel encima del compositor (tope, descanso, errores). */
function ChatNotice({ icon: Icon, title, body, role, note, action }: ChatNoticeProps) {
  return (
    <div role={role} className="readable-body mx-4 mb-2 rounded-2xl border border-accent/40 bg-accent/10 px-4 py-3 text-center">
      <Icon aria-hidden className="mx-auto mb-1 size-9 text-accent" />
      <p className="font-semibold text-accent">{title}</p>
      <p className="text-sm text-muted-foreground">{body}</p>
      {note && <p className="mt-1 text-sm text-muted-foreground">{note}</p>}
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="mt-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground"
        >
          {action.label}
        </button>
      )}
    </div>
  )
}

interface WaitNoticeProps {
  /** El tope del día es «a dormir» (luna); un descanso corto, un reloj de arena. */
  kind: 'limit' | 'rest'
  title: string
  /** Con el hueco `{time}`. */
  body: string
  /** Instante (ISO) en que se puede volver; `null` si el servidor no lo dijo. */
  until: string | null
  locale: string
  /** El tope del nivel gratis cuenta que un pack da más mensajes y ofrece verlos. */
  note?: string
  action?: ChatNoticeProps['action']
}

/** Tope diario o descanso tras un RATE_LIMITED: «podrás volver en {time}». */
export function WaitNotice({ kind, title, body, until, locale, note, action }: WaitNoticeProps) {
  const time = formatRetryDuration(secondsUntil(until, useClock()), locale)
  const icon = kind === 'limit' ? MoonStar : Hourglass
  return <ChatNotice icon={icon} role="status" title={title} body={body.replace('{time}', time)} note={note} action={action} />
}

/** Lo que dijo Papá Noel en un turno que no se guardó: aviso efímero, fuera del historial. */
export function FallbackNotice({ text, reason }: { text: string; reason: 'didNotHear' | 'elvesError' }) {
  // «No te he oído»: oreja tachada. «Los elfos se han enredado con los cables»: un cable.
  const Icon = reason === 'didNotHear' ? EarOff : Cable
  return (
    <p role="status" className="readable-body mx-4 mb-2 flex items-center gap-3 rounded-2xl bg-card px-4 py-2 text-sm text-card-foreground shadow">
      <Icon aria-hidden className="size-7 shrink-0 text-accent" />
      {text}
    </p>
  )
}

export function OpenErrorNotice(props: { title: string; body: string; retryLabel: string; onRetry: () => void }) {
  return (
    <ChatNotice icon={MailX} role="alert" title={props.title} body={props.body} action={{ label: props.retryLabel, onClick: props.onRetry }} />
  )
}

export function DeleteErrorNotice({ message }: { message: string }) {
  return (
    <p role="alert" className="readable-body mx-4 mb-2 rounded-2xl border border-destructive/40 bg-destructive/10 px-4 py-2 text-center text-sm text-destructive">
      {message}
    </p>
  )
}

interface SharedStatusProps {
  state: ChatState
  copy: Pick<SantaChatCopy, 'restTitle' | 'restBody'>
  locale: string
}

/**
 * Los avisos del estado que tienen los dos chats, en su orden: Papá Noel
 * descansando y, si no, lo que dijo en un turno sin guardar. `null` si no toca
 * ninguno: cada chat pone delante los suyos (tope, error al abrir, cierre…).
 */
export function sharedStatusNotice({ state, copy, locale }: SharedStatusProps): ReactElement | null {
  if (state.restUntil !== null) {
    return <WaitNotice kind="rest" title={copy.restTitle} body={copy.restBody} until={state.restUntil} locale={locale} />
  }
  if (state.fallbackNotice !== null) return <FallbackNotice text={state.fallbackNotice.text} reason={state.fallbackNotice.reason} />
  return null
}
